/**
 * DMC Marketplace Controller
 * Handles all marketplace interactions (mobile-first)
 * Version: 1.0
 */

class DMCMarketplace {
  constructor(sectionId) {
    this.sectionId = sectionId;
    this.isMobile = window.innerWidth < 1024;
    this.selectedProducts = new Set();
    this.activeFilters = {
      sort: 'price-low',
      availableOnly: true
    };
    
    this.init();
  }

  init() {
    this.cacheElements();
    this.bindEvents();
    this.initResponsiveListeners();
  }

  cacheElements() {
    const wrapper = document.getElementById(`dmc-marketplace-${this.sectionId}`);
    if (!wrapper) return;

    this.elements = {
      wrapper: wrapper,
      grid: wrapper.querySelector('.dmc-partners-grid'),
      emptyState: wrapper.querySelector('.dmc-empty-state'),
      
      // Tabs
      tabBtns: wrapper.querySelectorAll('.dmc-tab-btn'),
      
      // Desktop filters
      sortSelectDesktop: wrapper.querySelector(`#dmc-sort-${this.sectionId}`),
      compareToggleDesktop: wrapper.querySelector(`#dmc-compare-toggle-${this.sectionId}`),
      
      // Mobile filters
      openFiltersBtn: wrapper.querySelector(`#dmc-open-filters-${this.sectionId}`),
      filterSheet: wrapper.querySelector(`#dmc-filter-sheet-${this.sectionId}`),
      closeSheetBtn: wrapper.querySelector('.dmc-btn-close-sheet'),
      applySheetBtn: wrapper.querySelector('.dmc-btn-sheet-apply'),
      clearSheetBtn: wrapper.querySelector('.dmc-btn-sheet-clear'),
      sortRadiosMobile: wrapper.querySelectorAll(`input[name="sort-mobile-${this.sectionId}"]`),
      availableToggle: wrapper.querySelector(`#dmc-available-only-${this.sectionId}`),
      
      // Comparison
      compareBar: wrapper.querySelector(`#dmc-compare-bar-${this.sectionId}`),
      
      // Lead form
      leadModal: document.querySelector(`#dmc-lead-modal-${this.sectionId}`),
      leadForm: document.querySelector(`#dmc-lead-form-${this.sectionId}`)
    };
  }

  bindEvents() {
    // Tab switching
    this.elements.tabBtns?.forEach(btn => {
      btn.addEventListener('click', (e) => this.handleTabSwitch(e));
    });

    // Desktop sort
    this.elements.sortSelectDesktop?.addEventListener('change', (e) => {
      this.activeFilters.sort = e.target.value;
      this.applySorting();
    });

    // Mobile filter sheet
    this.elements.openFiltersBtn?.addEventListener('click', () => this.openFilterSheet());
    this.elements.closeSheetBtn?.addEventListener('click', () => this.closeFilterSheet());
    this.elements.applySheetBtn?.addEventListener('click', () => {
      this.applyMobileFilters();
      this.closeFilterSheet();
    });
    this.elements.clearSheetBtn?.addEventListener('click', () => this.clearFilters());

    // Backdrop click
    this.elements.filterSheet?.querySelector('.dmc-bottom-sheet-overlay')?.addEventListener('click', (e) => {
      if (e.target === e.currentTarget) {
        this.closeFilterSheet();
      }
    });

    // Comparison checkboxes
    this.elements.grid?.addEventListener('change', (e) => {
      if (e.target.matches('.dmc-compare-input, .dmc-compare-input-mobile')) {
        this.handleCompareCheckbox(e.target);
      }
    });

    // Action buttons
    this.elements.grid?.addEventListener('click', (e) => {
      const redirectBtn = e.target.closest('.dmc-btn-redirect');
      const leadBtn = e.target.closest('.dmc-btn-lead');
      
      if (redirectBtn) {
        e.preventDefault();
        this.handleRedirect(redirectBtn);
      } else if (leadBtn) {
        e.preventDefault();
        this.handleLeadForm(leadBtn);
      }
    });

    // Lead form
    if (this.elements.leadForm) {
      this.initLeadForm();
    }

    // Clear filters button
    this.elements.emptyState?.querySelector('.dmc-btn-clear-filters')?.addEventListener('click', () => {
      this.clearFilters();
    });
  }

  initResponsiveListeners() {
    let resizeTimer;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        const wasMobile = this.isMobile;
        this.isMobile = window.innerWidth < 1024;
        
        if (wasMobile !== this.isMobile) {
          this.handleScreenSizeChange();
        }
      }, 250);
    });
  }

  handleScreenSizeChange() {
    // Close mobile sheets when switching to desktop
    if (!this.isMobile && this.elements.filterSheet) {
      this.closeFilterSheet();
    }
  }

  handleTabSwitch(e) {
    const btn = e.currentTarget;
    const tab = btn.dataset.tab;
    
    // Update active state
    this.elements.tabBtns.forEach(b => b.classList.remove('active'));
    btn.classList.add('active');
    
    // Track event
    this.trackEvent('tab_switched', { tab: tab });
    
    // Here you can filter products based on tab if needed
    // For now, just visual feedback
  }

  openFilterSheet() {
    if (!this.elements.filterSheet) return;
    
    this.elements.filterSheet.style.display = 'block';
    document.body.style.overflow = 'hidden';
    
    // Trigger reflow for animation
    requestAnimationFrame(() => {
      this.elements.filterSheet.classList.add('active');
    });
  }

  closeFilterSheet() {
    if (!this.elements.filterSheet) return;
    
    this.elements.filterSheet.classList.remove('active');
    
    setTimeout(() => {
      this.elements.filterSheet.style.display = 'none';
      document.body.style.overflow = '';
    }, 300);
  }

  applyMobileFilters() {
    // Get sort value
    const checkedSort = Array.from(this.elements.sortRadiosMobile || [])
      .find(radio => radio.checked);
    
    if (checkedSort) {
      this.activeFilters.sort = checkedSort.value;
    }
    
    // Get availability filter
    if (this.elements.availableToggle) {
      this.activeFilters.availableOnly = this.elements.availableToggle.checked;
    }
    
    // Apply filters
    this.applySorting();
    this.applyAvailabilityFilter();
    
    // Update active filter badge
    this.updateFilterBadge();
  }

  applySorting() {
    const cards = Array.from(this.elements.grid?.querySelectorAll('.dmc-partner-card') || []);
    
    cards.sort((a, b) => {
      switch(this.activeFilters.sort) {
        case 'price-low':
          return parseFloat(a.dataset.price) - parseFloat(b.dataset.price);
        
        case 'price-high':
          return parseFloat(b.dataset.price) - parseFloat(a.dataset.price);
        
        case 'rating':
          const ratingA = parseFloat(a.dataset.rating) || 0;
          const ratingB = parseFloat(b.dataset.rating) || 0;
          return ratingB - ratingA;
        
        case 'delivery':
          // Extract first number from delivery days
          const deliveryA = this.extractDeliveryDays(a);
          const deliveryB = this.extractDeliveryDays(b);
          return deliveryA - deliveryB;
        
        case 'date':
          // For events - sort by start date
          const dateA = a.querySelector('[data-start-date]')?.dataset.startDate || '9999-12-31';
          const dateB = b.querySelector('[data-start-date]')?.dataset.startDate || '9999-12-31';
          return dateA.localeCompare(dateB);
        
        default:
          return 0;
      }
    });

    // Re-append sorted cards
    cards.forEach(card => this.elements.grid?.appendChild(card));
    
    this.trackEvent('sorting_applied', { sort_by: this.activeFilters.sort });
  }

  extractDeliveryDays(card) {
    const deliveryText = card.querySelector('.dmc-meta-item span')?.textContent || '999';
    const match = deliveryText.match(/(\d+)/);
    return match ? parseInt(match[1]) : 999;
  }

  applyAvailabilityFilter() {
    const cards = this.elements.grid?.querySelectorAll('.dmc-partner-card') || [];
    let visibleCount = 0;
    
    cards.forEach(card => {
      const availability = card.dataset.availability;
      const isAvailable = availability === 'available';
      
      if (!this.activeFilters.availableOnly || isAvailable) {
        card.style.display = '';
        visibleCount++;
      } else {
        card.style.display = 'none';
      }
    });
    
    // Show/hide empty state
    this.toggleEmptyState(visibleCount === 0);
  }

  clearFilters() {
    // Reset to defaults
    this.activeFilters = {
      sort: 'price-low',
      availableOnly: true
    };
    
    // Update desktop
    if (this.elements.sortSelectDesktop) {
      this.elements.sortSelectDesktop.value = 'price-low';
    }
    
    // Update mobile
    this.elements.sortRadiosMobile?.forEach(radio => {
      radio.checked = radio.value === 'price-low';
    });
    
    if (this.elements.availableToggle) {
      this.elements.availableToggle.checked = true;
    }
    
    // Show all cards
    const cards = this.elements.grid?.querySelectorAll('.dmc-partner-card') || [];
    cards.forEach(card => card.style.display = '');
    
    // Apply default sorting
    this.applySorting();
    
    // Hide empty state
    this.toggleEmptyState(false);
    
    // Update badge
    this.updateFilterBadge();
    
    this.trackEvent('filters_cleared');
  }

  updateFilterBadge() {
    const badge = this.elements.openFiltersBtn?.querySelector('.dmc-active-filter-badge');
    if (!badge) return;
    
    let activeCount = 0;
    
    if (this.activeFilters.sort !== 'price-low') activeCount++;
    if (!this.activeFilters.availableOnly) activeCount++;
    
    if (activeCount > 0) {
      badge.textContent = activeCount;
      badge.style.display = 'block';
    } else {
      badge.style.display = 'none';
    }
  }

  toggleEmptyState(show) {
    if (this.elements.emptyState) {
      this.elements.emptyState.style.display = show ? 'flex' : 'none';
    }
    if (this.elements.grid) {
      this.elements.grid.style.display = show ? 'none' : 'grid';
    }
  }

  // Comparison functionality
  handleCompareCheckbox(checkbox) {
    const productId = checkbox.dataset.productId;
    const productTitle = checkbox.dataset.productTitle;
    const partnerName = checkbox.dataset.partnerName;
    
    if (checkbox.checked) {
      // Check max limit
      const enableComparison = this.elements.grid?.dataset.enableComparison === 'true';
      const maxItems = 4; // Default
      
      if (this.selectedProducts.size >= maxItems) {
        checkbox.checked = false;
        this.showToast(`Maximum ${maxItems} products can be compared`, 'warning');
        return;
      }
      
      this.selectedProducts.add({
        id: productId,
        title: productTitle,
        partner: partnerName
      });
    } else {
      // Remove from set
      this.selectedProducts.forEach(item => {
        if (item.id === productId) {
          this.selectedProducts.delete(item);
        }
      });
    }
    
    this.updateComparisonUI();
  }

  updateComparisonUI() {
    const count = this.selectedProducts.size;
    
    // Update count displays
    const countElements = document.querySelectorAll('.dmc-compare-count, .dmc-compare-count-mobile');
    countElements.forEach(el => el.textContent = count);
    
    // Show/hide comparison UI
    if (count >= 2) {
      this.elements.compareBar?.style.setProperty('display', 'flex');
      this.elements.compareToggleDesktop?.style.setProperty('display', 'flex');
    } else {
      this.elements.compareBar?.style.setProperty('display', 'none');
      this.elements.compareToggleDesktop?.style.setProperty('display', 'none');
    }
  }

  // Redirect handling
  handleRedirect(btn) {
    const partnerId = btn.dataset.partnerId;
    const partnerName = btn.dataset.partnerName;
    const redirectUrl = btn.dataset.redirectUrl;
    const productType = btn.dataset.productType;
    
    if (!redirectUrl) {
      console.error('No redirect URL provided');
      return;
    }
    
    this.trackEvent('redirect_initiated', {
      partner_id: partnerId,
      partner_name: partnerName,
      product_type: productType
    });
    
    // Show confirmation modal
    this.showRedirectConfirmation(partnerName, redirectUrl, partnerId);
  }

  showRedirectConfirmation(partnerName, url, partnerId) {
    const modal = document.createElement('div');
    modal.className = 'dmc-redirect-modal';
    modal.innerHTML = `
      <div class="dmc-modal-overlay"></div>
      <div class="dmc-modal-content" style="max-width: 400px; padding: 24px; text-align: center;">
        <div style="margin-bottom: 16px;">
          <svg width="64" height="64" fill="#007bff" viewBox="0 0 24 24">
            <path d="M19 19H5V5h7V3H5c-1.11 0-2 .9-2 2v14c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2v-7h-2v7zM14 3v2h3.59l-9.83 9.83 1.41 1.41L19 6.41V10h2V3h-7z"/>
          </svg>
        </div>
        <h3 style="font-size: 20px; font-weight: 700; margin: 0 0 8px;">Redirecting to ${partnerName}</h3>
        <p style="font-size: 15px; color: #666; margin: 0 0 20px;">You'll be redirected to their website to complete your booking.</p>
        <p style="font-size: 14px; color: #999; margin: 0 0 24px;">Redirecting in <strong id="dmc-countdown">5</strong> seconds...</p>
        <div style="display: flex; gap: 12px;">
          <button type="button" class="dmc-btn-cancel-redirect" style="flex: 1; padding: 12px; background: #f5f5f5; border: none; border-radius: 10px; font-weight: 600; cursor: pointer;">Stay Here</button>
          <button type="button" class="dmc-btn-confirm-redirect" style="flex: 1; padding: 12px; background: #007bff; color: #fff; border: none; border-radius: 10px; font-weight: 600; cursor: pointer;">Continue Now</button>
        </div>
      </div>
    `;
    
    document.body.appendChild(modal);
    document.body.style.overflow = 'hidden';
    
    // Countdown
    let countdown = 5;
    const countdownEl = modal.querySelector('#dmc-countdown');
    const interval = setInterval(() => {
      countdown--;
      if (countdownEl) countdownEl.textContent = countdown;
      
      if (countdown <= 0) {
        clearInterval(interval);
        this.executeRedirect(url, partnerId);
        modal.remove();
      }
    }, 1000);
    
    // Continue button
    modal.querySelector('.dmc-btn-confirm-redirect')?.addEventListener('click', () => {
      clearInterval(interval);
      this.executeRedirect(url, partnerId);
      modal.remove();
    });
    
    // Cancel button
    modal.querySelector('.dmc-btn-cancel-redirect')?.addEventListener('click', () => {
      clearInterval(interval);
      modal.remove();
      document.body.style.overflow = '';
    });
    
    // Backdrop click
    modal.querySelector('.dmc-modal-overlay')?.addEventListener('click', () => {
      clearInterval(interval);
      modal.remove();
      document.body.style.overflow = '';
    });
  }

  executeRedirect(url, partnerId) {
    // Track conversion
    this.trackEvent('redirect_completed', { 
      partner_id: partnerId,
      destination: url
    });
    
    // Record click for commission tracking
    this.recordPartnerClick(partnerId, 'redirect');
    
    // Redirect
    window.open(url, '_blank', 'noopener,noreferrer');
    document.body.style.overflow = '';
  }

  // Lead form handling
  handleLeadForm(btn) {
    const partnerId = btn.dataset.partnerId;
    const partnerName = btn.dataset.partnerName;
    const productTitle = btn.dataset.productTitle;
    const productType = btn.dataset.productType;
    const notificationType = btn.dataset.notificationType;
    const contactEmail = btn.dataset.contactEmail;
    const contactPhone = btn.dataset.contactPhone;
    
    if (!this.elements.leadModal) return;
    
    // Populate hidden fields
    document.getElementById(`dmc-partner-id-${this.sectionId}`).value = partnerId;
    document.getElementById(`dmc-partner-name-${this.sectionId}`).value = partnerName;
    document.getElementById(`dmc-product-title-${this.sectionId}`).value = productTitle;
    document.getElementById(`dmc-product-type-${this.sectionId}`).value = productType;
    document.getElementById(`dmc-notification-type-${this.sectionId}`).value = notificationType;
    document.getElementById(`dmc-contact-email-${this.sectionId}`).value = contactEmail || '';
    document.getElementById(`dmc-contact-phone-${this.sectionId}`).value = contactPhone || '';
    
    // Update modal title
    let title = 'Get in Touch';
    if (productType === 'event') title = 'Register Your Interest';
    else if (productType === 'stay') title = 'Check Availability';
    else if (productType === 'rental_partner') title = 'Request Rental Quote';
    
    const titleEl = document.getElementById(`dmc-modal-title-${this.sectionId}`);
    if (titleEl) titleEl.textContent = title;
    
    // Show/hide conditional fields
    const dateGroup = this.elements.leadModal.querySelector('.dmc-date-group');
    const peopleGroup = this.elements.leadModal.querySelector('.dmc-people-group');
    
    if (productType === 'event' || productType === 'stay') {
      if (dateGroup) dateGroup.style.display = 'block';
      if (peopleGroup) peopleGroup.style.display = 'block';
    } else {
      if (dateGroup) dateGroup.style.display = 'none';
      if (peopleGroup) peopleGroup.style.display = 'none';
    }
    
    // Show modal
    this.elements.leadModal.style.display = 'flex';
    document.body.style.overflow = 'hidden';
    
    this.trackEvent('lead_form_opened', {
      partner_id: partnerId,
      partner_name: partnerName,
      product_type: productType
    });
  }

  initLeadForm() {
    const modal = this.elements.leadModal;
    if (!modal) return;
    
    const form = this.elements.leadForm;
    const closeBtn = modal.querySelector('.dmc-btn-close-modal');
    const cancelBtn = modal.querySelector('.dmc-btn-cancel');
    const successDiv = modal.querySelector('.dmc-success-message');
    const successCloseBtn = modal.querySelector('.dmc-btn-success-close');
    
    const closeModal = () => {
      modal.style.display = 'none';
      document.body.style.overflow = '';
      form?.reset();
      form.style.display = 'block';
      successDiv.style.display = 'none';
    };
    
    closeBtn?.addEventListener('click', closeModal);
    cancelBtn?.addEventListener('click', closeModal);
    successCloseBtn?.addEventListener('click', closeModal);
    modal.querySelector('.dmc-modal-overlay')?.addEventListener('click', closeModal);
    
    // Form submission
    form?.addEventListener('submit', async (e) => {
      e.preventDefault();
      
      const submitBtn = form.querySelector('.dmc-btn-submit');
      const btnText = submitBtn.querySelector('.dmc-btn-text');
      const btnLoading = submitBtn.querySelector('.dmc-btn-loading');
      
      // Show loading
      submitBtn.disabled = true;
      btnText.style.display = 'none';
      btnLoading.style.display = 'flex';
      
      // Collect form data
      const formData = new FormData(form);
      const data = Object.fromEntries(formData.entries());
      
      try {
        // Send lead notification
        const result = await this.sendLeadNotification(data);
        
        if (result.success) {
          // Show success message
          form.style.display = 'none';
          successDiv.style.display = 'block';
          
          // Customize success message
          const notificationType = data.notification_type;
          const partnerName = data.partner_name;
          let successMsg = `${partnerName} will contact you shortly at your provided email/phone.`;
          
          if (notificationType === 'whatsapp') {
            successMsg = `${partnerName} will reach out to you on WhatsApp within a few hours.`;
          } else if (notificationType === 'email') {
            successMsg = `${partnerName} has been notified and will email you soon.`;
          }
          
          const successText = document.getElementById(`dmc-success-text-${this.sectionId}`);
          if (successText) successText.textContent = successMsg;
          
          // Track conversion
          this.trackEvent('lead_submitted', {
            partner_id: data.partner_id,
            partner_name: data.partner_name,
            product_type: data.product_type,
            notification_type: notificationType
          });
          
          // Record for commission tracking
          this.recordPartnerClick(data.partner_id, 'lead');
          
          // Auto-close after 5 seconds
          setTimeout(closeModal, 5000);
        } else {
          throw new Error('Submission failed');
        }
      } catch (error) {
        this.showToast('Something went wrong. Please try again or contact us directly.', 'error');
        console.error('Lead submission error:', error);
      } finally {
        submitBtn.disabled = false;
        btnText.style.display = 'inline';
        btnLoading.style.display = 'none';
      }
    });
  }

  async sendLeadNotification(data) {
    // You'll replace this with your actual webhook endpoint (N8N, Zapier, or custom backend)
    const webhookUrl = '/apps/dmc-marketplace-leads'; // Change this to your endpoint
    
    try {
      const response = await fetch(webhookUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          ...data,
          timestamp: new Date().toISOString(),
          source: 'dmc_marketplace',
          user_agent: navigator.userAgent
        })
      });
      
      if (response.ok) {
        return { success: true };
      } else {
        // Fallback: Try sending email via Shopify form
        return this.sendViaShopifyForm(data);
      }
    } catch (error) {
      console.error('Webhook error:', error);
      // Fallback to Shopify form
      return this.sendViaShopifyForm(data);
    }
  }

  async sendViaShopifyForm(data) {
    // Fallback: Use Shopify contact form
    try {
      const formData = new FormData();
      formData.append('contact[name]', data.name);
      formData.append('contact[email]', data.email);
      formData.append('contact[phone]', data.phone);
      formData.append('contact[body]', `
        Lead from DMC Marketplace
        Partner: ${data.partner_name}
        Product: ${data.product_title}
        Type: ${data.product_type}
        
        Message: ${data.message || 'No message'}
        
        ${data.date_start ? `Dates: ${data.date_start} to ${data.date_end}` : ''}
        ${data.people_count ? `People: ${data.people_count}` : ''}
        
        Contact Email: ${data.contact_email || 'N/A'}
        Contact Phone: ${data.contact_phone || 'N/A'}
      `);
      
      const response = await fetch('/contact', {
        method: 'POST',
        body: formData
      });
      
      return { success: response.ok };
    } catch (error) {
      console.error('Shopify form error:', error);
      return { success: false };
    }
  }

  recordPartnerClick(partnerId, type) {
    // Record click for commission tracking
    const data = {
      partner_id: partnerId,
      type: type, // 'redirect' or 'lead'
      timestamp: Date.now(),
      referrer: window.location.href,
      session_id: this.getSessionId()
    };
    
    // Send to tracking endpoint
    fetch('/apps/dmc-track-partner-click', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    }).catch(err => console.error('Click tracking failed:', err));
    
    // Also store in localStorage for client-side tracking
    const clicks = JSON.parse(localStorage.getItem('dmc_partner_clicks') || '[]');
    clicks.push(data);
    localStorage.setItem('dmc_partner_clicks', JSON.stringify(clicks.slice(-100))); // Keep last 100
  }

  getSessionId() {
    let sessionId = sessionStorage.getItem('dmc_session_id');
    if (!sessionId) {
      sessionId = 'dmc_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
      sessionStorage.setItem('dmc_session_id', sessionId);
    }
    return sessionId;
  }

  trackEvent(eventName, params = {}) {
    // Google Analytics
    if (typeof gtag !== 'undefined') {
      gtag('event', eventName, {
        ...params,
        event_category: 'dmc_marketplace',
        device_type: this.isMobile ? 'mobile' : 'desktop'
      });
    }
    
    // Facebook Pixel
    if (typeof fbq !== 'undefined') {
      fbq('trackCustom', eventName, params);
    }
    
    // Console log for debugging
    console.log('DMC Event:', eventName, params);
  }

  showToast(message, type = 'info') {
    const toast = document.createElement('div');
    toast.className = `dmc-toast dmc-toast-${type}`;
    toast.style.cssText = `
      position: fixed;
      bottom: 80px;
      left: 50%;
      transform: translateX(-50%);
      background: ${type === 'error' ? '#f44336' : type === 'warning' ? '#ff9800' : '#333'};
      color: #fff;
      padding: 12px 24px;
      border-radius: 8px;
      font-size: 14px;
      font-weight: 600;
      z-index: 10001;
      box-shadow: 0 4px 12px rgba(0,0,0,0.3);
      animation: slideUpFade 0.3s ease;
    `;
    toast.textContent = message;
    
    document.body.appendChild(toast);
    
    setTimeout(() => {
      toast.style.animation = 'slideDownFade 0.3s ease';
      setTimeout(() => toast.remove(), 300);
    }, 3000);
  }
}

// Auto-initialize on page load
document.addEventListener('DOMContentLoaded', function() {
  // Find all marketplace sections
  const marketplaceSections = document.querySelectorAll('[id^="dmc-marketplace-"]');
  
  marketplaceSections.forEach(section => {
    const sectionId = section.dataset.sectionId;
    if (sectionId) {
      new DMCMarketplace(sectionId);
    }
  });
});

// Add CSS for toast animations
const style = document.createElement('style');
style.textContent = `
  @keyframes slideUpFade {
    from {
      opacity: 0;
      transform: translateX(-50%) translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateX(-50%) translateY(0);
    }
  }
  
  @keyframes slideDownFade {
    from {
      opacity: 1;
      transform: translateX(-50%) translateY(0);
    }
    to {
      opacity: 0;
      transform: translateX(-50%) translateY(20px);
    }
  }
`;
document.head.appendChild(style);
